/**
 * units — a unidade de medida do catálogo E do orçamento, num vocabulário só.
 *
 * ==========================================================================
 * O PROBLEMA QUE ISTO RESOLVE (é de DADOS, não de código)
 * ==========================================================================
 * O app tinha DOIS vocabulários incompatíveis, cada um duplicado em vários
 * arquivos:
 *
 *   catálogo (6):  hour · day · service · m2 · installation · budget
 *   orçamento (18+): h · min · di · sem · mes · ano · hr_homem · srv · vb ·
 *                    un · vis · km · pg · palav · lauda · user · lic · pt
 *
 * Só `m2` coincide. `hour` ≠ `h`, `day` ≠ `di`, `service` ≠ `srv`,
 * `budget` ≠ `vb`. Consequência real: um serviço cadastrado como `hour` cai,
 * no orçamento, num `<Select>` cujas opções não contêm `hour` — o Radix então
 * exibe o PLACEHOLDER ("Selecione a unidade"). O valor continua no estado e é
 * gravado, mas o usuário vê a unidade como se estivesse em branco. Nenhum erro,
 * nenhum log. E na planilha (`xlsxTemplates`) acontecia o inverso: um item de
 * orçamento com `h` caía num mapa que só conhecia `hour` e a célula saía com o
 * código cru ("h") em vez do rótulo.
 *
 * ==========================================================================
 * A DECISÃO
 * ==========================================================================
 * 1. **Canônico = o vocabulário do ORÇAMENTO** (mais rico, agrupado e é o que
 *    as linhas de `quote_items` em produção realmente contêm).
 * 2. **Normalização na leitura**, não migração de dados. Motivo abaixo.
 * 3. Três códigos NOVOS entram no canônico para não perder significado ao
 *    traduzir o vocabulário antigo: `inst` (Instalação), `m2`/`m` para
 *    SERVIÇOS (o catálogo já oferecia m² a serviço) e `mlin` (Metro linear).
 *
 * POR QUE NORMALIZAÇÃO E NÃO MIGRAÇÃO: em produção existem **zero** linhas com
 * os códigos antigos (conferido em `products` e `quote_items` em 2026-07-25),
 * então a migração seria um no-op — e ela tem um risco que a normalização não
 * tem, descrito em `ml` logo abaixo. Normalizando, uma linha antiga que apareça
 * de qualquer outra origem (cache de device, backup, import) continua sendo
 * lida com o rótulo certo, e se auto-corrige na próxima edição, porque o
 * formulário grava o código canônico.
 *
 * ==========================================================================
 * `ml`: A COLISÃO QUE NÃO PODE SER "CORRIGIDA"
 * ==========================================================================
 * No catálogo, `ml` era rotulado **"Metro linear"**. No orçamento, `ml` é
 * **"Mililitro"**. É o mesmo código com dois significados, e não há como saber
 * qual foi a intenção de uma linha existente: produtos criados pelo formulário
 * do catálogo queriam dizer metro linear, mas produtos criados pelo atalho
 * "Salvar este item no meu catálogo" copiam a unidade do ORÇAMENTO e querem
 * dizer mililitro. Renomear em massa corromperia um dos dois grupos em
 * silêncio.
 *
 * Portanto: `ml` permanece **Mililitro** (o significado do vocabulário
 * canônico) e "Metro linear" ganha um código novo e inequívoco, `mlin`.
 * `ml` NÃO é traduzido por `normalizeUnit` — e não há nenhuma linha `ml` em
 * produção, então nada muda de sentido hoje. Ver CLAUDE.md #64.
 */

export interface UnitOption {
  value: string;
  /** Rótulo completo, usado nos `<Select>` e na planilha. */
  label: string;
  /** Abreviação curta (≤ 5 chars) para a coluna estreita do PDF. */
  short: string;
}

export interface UnitGroup {
  label: string;
  options: UnitOption[];
}

export type UnitKind = 'service' | 'product';

/** Unidades de SERVIÇO, agrupadas (o que o `<Select>` renderiza). */
export const SERVICE_UNIT_GROUPS: UnitGroup[] = [
  {
    label: 'Tempo',
    options: [
      { value: 'h', label: 'Hora (h)', short: 'h' },
      { value: 'min', label: 'Minuto (min)', short: 'min' },
      { value: 'di', label: 'Diária (di)', short: 'di' },
      { value: 'sem', label: 'Semana (sem)', short: 'sem' },
      { value: 'mes', label: 'Mês', short: 'mês' },
      { value: 'ano', label: 'Ano', short: 'ano' },
      { value: 'hr_homem', label: 'Homem-hora (hr/homem)', short: 'h/hom' },
    ],
  },
  {
    label: 'Execução',
    options: [
      { value: 'srv', label: 'Serviço (srv)', short: 'srv' },
      // NOVO no canônico: recebe o legado `installation` do catálogo. Sem ele,
      // "Instalação" viraria "Serviço" e o prestador perderia a distinção que
      // ele mesmo tinha cadastrado.
      { value: 'inst', label: 'Instalação (inst)', short: 'inst' },
      { value: 'vb', label: 'Verba (vb)', short: 'vb' },
      { value: 'un', label: 'Unidade (un)', short: 'un' },
      { value: 'vis', label: 'Visita técnica (vis)', short: 'vis' },
      { value: 'km', label: 'Quilômetro (km)', short: 'km' },
    ],
  },
  {
    // NOVO grupo: o catálogo já oferecia m² a serviço (pintura, piso, forro) e
    // o orçamento não tinha nenhuma medida de área para serviço.
    label: 'Medida',
    options: [
      { value: 'm', label: 'Metro (m)', short: 'm' },
      { value: 'm2', label: 'Metro quadrado (m²)', short: 'm²' },
    ],
  },
  {
    label: 'Produção',
    options: [
      { value: 'pg', label: 'Página (pg)', short: 'pg' },
      { value: 'palav', label: 'Palavra (palav)', short: 'palav' },
      { value: 'lauda', label: 'Lauda', short: 'lauda' },
    ],
  },
  {
    label: 'Digital',
    options: [
      { value: 'user', label: 'Usuário (user)', short: 'user' },
      { value: 'lic', label: 'Licença (lic)', short: 'lic' },
      { value: 'pt', label: 'Ponto (pt)', short: 'pt' },
    ],
  },
];

/** Unidades de PRODUTO, agrupadas. */
export const PRODUCT_UNIT_GROUPS: UnitGroup[] = [
  {
    label: 'Contagem',
    options: [
      { value: 'un', label: 'Unidade (un)', short: 'un' },
      { value: 'pc', label: 'Peça (pc)', short: 'pc' },
      { value: 'cj', label: 'Conjunto (cj)', short: 'cj' },
      { value: 'kit', label: 'Kit', short: 'kit' },
      { value: 'pa', label: 'Par (pa)', short: 'par' },
      { value: 'dz', label: 'Dúzia (dz)', short: 'dz' },
      { value: 'ct', label: 'Cento (ct)', short: 'ct' },
      { value: 'mil', label: 'Milheiro (mil)', short: 'mil' },
    ],
  },
  {
    label: 'Medidas',
    options: [
      { value: 'm', label: 'Metro (m)', short: 'm' },
      // NOVO: "Metro linear" do catálogo antigo, com código próprio para não
      // colidir com `ml` (mililitro). Ver o cabeçalho deste arquivo.
      { value: 'mlin', label: 'Metro linear (m linear)', short: 'm lin' },
      { value: 'cm', label: 'Centímetro (cm)', short: 'cm' },
      { value: 'mm', label: 'Milímetro (mm)', short: 'mm' },
      { value: 'pol', label: 'Polegada (pol)', short: 'pol' },
      { value: 'm2', label: 'Metro quadrado (m²)', short: 'm²' },
      { value: 'm3', label: 'Metro cúbico (m³)', short: 'm³' },
    ],
  },
  {
    label: 'Peso/Volume',
    options: [
      { value: 'kg', label: 'Quilograma (kg)', short: 'kg' },
      { value: 'g', label: 'Grama (g)', short: 'g' },
      { value: 't', label: 'Tonelada (t)', short: 't' },
      { value: 'l', label: 'Litro (l)', short: 'l' },
      { value: 'ml', label: 'Mililitro (ml)', short: 'ml' },
    ],
  },
  {
    label: 'Embalagem',
    options: [
      { value: 'cx', label: 'Caixa (cx)', short: 'cx' },
      { value: 'pct', label: 'Pacote (pct)', short: 'pct' },
      { value: 'fd', label: 'Fardo (fd)', short: 'fd' },
      { value: 'rl', label: 'Rolo (rl)', short: 'rl' },
      { value: 'bb', label: 'Bobina (bb)', short: 'bb' },
      { value: 'bd', label: 'Balde (bd)', short: 'bd' },
      { value: 'sac', label: 'Saco (sac)', short: 'sac' },
    ],
  },
];

/**
 * Vocabulário ANTIGO → canônico.
 *
 * Inclui os 6 códigos do catálogo, os 2 de produto que divergiam (`box`,
 * `pack`) e as formas por extenso que o `unitMap` do PDF já tratava — assim o
 * PDF não perde nada ao passar a usar este módulo.
 *
 * `ml` está DELIBERADAMENTE fora (ver o cabeçalho).
 */
export const LEGACY_UNIT_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  // catálogo → orçamento (serviços)
  hour: 'h',
  hora: 'h',
  day: 'di',
  dia: 'di',
  service: 'srv',
  servico: 'srv',
  serviço: 'srv',
  installation: 'inst',
  instalacao: 'inst',
  instalação: 'inst',
  budget: 'vb',
  verba: 'vb',
  // catálogo → orçamento (produtos)
  box: 'cx',
  caixa: 'cx',
  pack: 'pct',
  package: 'pct',
  pacote: 'pct',
  unit: 'un',
  unidade: 'un',
  meter: 'm',
  metro: 'm',
  kilogram: 'kg',
  quilograma: 'kg',
  liter: 'l',
  litro: 'l',
});

const ALL_OPTIONS: UnitOption[] = [...SERVICE_UNIT_GROUPS, ...PRODUCT_UNIT_GROUPS].flatMap((g) => g.options);

const OPTION_BY_VALUE = new Map<string, UnitOption>(
  // Produto por último de propósito? Não: os códigos compartilhados (`un`, `m`,
  // `kg`, `l`, `m2`) têm o MESMO rótulo nos dois lados, então a ordem é
  // irrelevante — e um `set` repetido apenas sobrescreve com o equivalente.
  ALL_OPTIONS.map((o) => [o.value, o]),
);

function groupsFor(kind: UnitKind | null | undefined): UnitGroup[] {
  return kind === 'product' ? PRODUCT_UNIT_GROUPS : SERVICE_UNIT_GROUPS;
}

/** Os valores válidos de um tipo (usado por validação e testes). */
export function unitValues(kind: UnitKind): string[] {
  return groupsFor(kind).flatMap((g) => g.options.map((o) => o.value));
}

/**
 * Traduz um código antigo para o canônico. Devolve `null` para vazio/ausente e
 * o próprio valor (em minúsculas) quando ele já é canônico ou é desconhecido —
 * NUNCA descarta: um código que este módulo não conhece ainda é o dado do
 * usuário, e sumir com ele seria pior do que exibi-lo cru.
 */
export function normalizeUnit(unit: string | null | undefined): string | null {
  const raw = String(unit ?? '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return LEGACY_UNIT_ALIASES[lower] ?? lower;
}

/** O código é conhecido no vocabulário deste tipo (depois de normalizado)? */
export function isKnownUnit(unit: string | null | undefined, kind: UnitKind): boolean {
  const normalized = normalizeUnit(unit);
  if (!normalized) return false;
  return unitValues(kind).includes(normalized);
}

/**
 * Rótulo completo de uma unidade.
 *
 * Procura primeiro no vocabulário do tipo informado e depois no outro: um
 * produto salvo pelo atalho do orçamento pode carregar uma unidade de serviço
 * (e vice-versa), e mostrar o código cru nesse caso seria uma regressão.
 * Sem correspondência nenhuma, devolve o valor original.
 */
export function unitLabel(unit: string | null | undefined, kind?: UnitKind | null): string {
  const normalized = normalizeUnit(unit);
  if (!normalized) return '';
  const own = groupsFor(kind)
    .flatMap((g) => g.options)
    .find((o) => o.value === normalized);
  if (own) return own.label;
  return OPTION_BY_VALUE.get(normalized)?.label ?? String(unit ?? '');
}

/**
 * Abreviação para a coluna "Un." do PDF, que é estreita.
 *
 * Antes existia um `unitMap` próprio em `pdfTemplates/shared.ts` que conhecia
 * meia dúzia de códigos em inglês e, para todo o resto, cortava a string nos 3
 * primeiros caracteres — `hr_homem` virava "hr_" e `lauda` virava "lau".
 */
export function unitShortLabel(unit: string | null | undefined): string {
  const normalized = normalizeUnit(unit);
  if (!normalized) return '';
  const known = OPTION_BY_VALUE.get(normalized);
  if (known) return known.short;
  // Código desconhecido: mostra os 5 primeiros caracteres (a coluna não
  // comporta mais) em vez de sumir com o dado.
  return normalized.slice(0, 5);
}

/**
 * Garante que o valor exibido no `<Select>` exista entre as opções.
 *
 * Um `<Select>` do Radix com `value` fora das opções renderiza o PLACEHOLDER —
 * o usuário lê "Selecione a unidade" num item que TEM unidade. Este helper
 * devolve os grupos com uma opção extra ("Unidade atual") quando o valor
 * normalizado não pertence ao vocabulário do tipo, de modo que o dado do
 * usuário continue visível e selecionado.
 */
export function unitGroupsIncluding(unit: string | null | undefined, kind: UnitKind): UnitGroup[] {
  const groups = groupsFor(kind);
  const normalized = normalizeUnit(unit);
  if (!normalized || isKnownUnit(normalized, kind)) return groups;

  return [
    ...groups,
    {
      label: 'Unidade atual',
      options: [{ value: normalized, label: unitLabel(normalized, kind), short: unitShortLabel(normalized) }],
    },
  ];
}
